/**
 * T.Z219 — the register's column visibility: a purely presentational show/hide over
 * {@link REGISTER_HEADERS}.
 *
 * SERVER BEHAVIOUR IS UNTOUCHED: #3 still answers every field of every row (no
 * contract change, no request change) and #18 keeps its own column set. Hiding a
 * column removes its header, its cells and (with them) its sort control from the
 * SCREEN only — a hidden sortable column is simply not clickable while hidden,
 * which is the same «a control must not 400» honesty the `sortable: false` flag
 * carries, just user-driven instead of build-driven.
 *
 * The choice persists in `localStorage` under a VERSIONED key
 * ({@link COLUMNS_STORAGE_KEY}) as a JSON array of the VISIBLE column ids:
 *   * a missing or corrupt key → every column visible (the defaults);
 *   * an id the current build does not know is IGNORED (a column the table has
 *     since dropped, a hand-edited value) — schema evolution can never wedge the
 *     page;
 *   * an id left OUT of the array is a hidden column;
 *   * an empty surviving set → the defaults (the table is never all-hidden; the
 *     control refuses to uncheck the last enabled column anyway, so this arm is
 *     only reachable by a hand-edited or stale value).
 *
 * The key follows the `cbm-`/`cbm.` prefix convention of the only other stored
 * choice (`cbm-theme`); the `.v1` suffix is the bump handle — changing the SHAPE
 * means writing `cbm.applications.columns.v2`, never re-reading v1 loosely.
 */

import { REGISTER_HEADERS, type RegisterColumn } from './applications-model';

/** The versioned `localStorage` key. Changing the shape means a new suffix. */
export const COLUMNS_STORAGE_KEY = 'cbm.applications.columns.v1';

/** The defaults: every rendered column of the current build, in table order. */
export function defaultVisibleColumns(columns: ReadonlyArray<RegisterColumn> = REGISTER_HEADERS): string[] {
  return columns.map((column) => column.id);
}

/**
 * Stored text → the visible ids of THIS build. `null` (no key), a non-array, an
 * array of non-strings, or an empty surviving set all fold to the defaults; an id
 * the build does not know is dropped.
 */
export function parseVisibleColumns(
  raw: string | null | undefined,
  columns: ReadonlyArray<RegisterColumn> = REGISTER_HEADERS,
): string[] {
  const defaults = defaultVisibleColumns(columns);
  if (typeof raw !== 'string') return defaults;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return defaults; // corrupt JSON is a missing key, never a half-hidden table
  }
  if (!Array.isArray(parsed)) return defaults;
  const known = new Set(defaults);
  const visible = parsed.filter((entry): entry is string => typeof entry === 'string' && known.has(entry));
  return visible.length > 0 ? visible : defaults;
}

/** The visible ids → the stored text (the key's own, versioned shape). */
export function serializeVisibleColumns(ids: ReadonlyArray<string>): string {
  return JSON.stringify([...ids]);
}

/** Reads the stored choice through the given storage (injected for the tests). */
export function readVisibleColumns(
  storage: Pick<Storage, 'getItem'>,
  columns: ReadonlyArray<RegisterColumn> = REGISTER_HEADERS,
): string[] {
  try {
    return parseVisibleColumns(storage.getItem(COLUMNS_STORAGE_KEY), columns);
  } catch {
    // Private mode / storage disabled: the defaults, and the writes below are
    // refused the same quiet way.
    return defaultVisibleColumns(columns);
  }
}

/** Persists the choice through the given storage (injected for the tests). */
export function writeVisibleColumns(storage: Pick<Storage, 'setItem'>, ids: ReadonlyArray<string>): void {
  try {
    storage.setItem(COLUMNS_STORAGE_KEY, serializeVisibleColumns(ids));
  } catch {
    // Storage disabled: the choice still applies for this page, like the theme's.
  }
}

/**
 * One checkbox click. Checking a column puts it back into the set (table order is
 * owned by {@link REGISTER_HEADERS}, not by the stored array); unchecking removes
 * it — UNLESS it is the last enabled column, which the control refuses (a table
 * with no columns is not a view, it is a bug). The refusal here is the same rule
 * the disabled checkbox in the popover shows the user.
 */
export function toggleVisibleColumn(
  visible: ReadonlyArray<string>,
  id: string,
  columns: ReadonlyArray<RegisterColumn> = REGISTER_HEADERS,
): string[] {
  const isShown = visible.includes(id);
  if (!isShown) {
    // Reinsert at the table position so a re-checked column reappears where the
    // headers render it — the stored array's own order is never load-bearing.
    const tableOrder = defaultVisibleColumns(columns);
    const next = tableOrder.filter((columnId) => columnId === id || visible.includes(columnId));
    return next;
  }
  if (visible.length <= 1) return [...visible]; // the last column cannot go
  return visible.filter((columnId) => columnId !== id);
}

/** «Сбросить» inside the popover: every column of the current build, visible. */
export function resetVisibleColumns(columns: ReadonlyArray<RegisterColumn> = REGISTER_HEADERS): string[] {
  return defaultVisibleColumns(columns);
}

/** The headers the table renders: {@link REGISTER_HEADERS} minus the hidden ones. */
export function visibleColumnsOf(
  columns: ReadonlyArray<RegisterColumn>,
  visible: ReadonlyArray<string>,
): ReadonlyArray<RegisterColumn> {
  const shown = new Set(visible);
  return columns.filter((column) => shown.has(column.id));
}

/**
 * ADDITIVE (v1.9.79, T.Z220) — the register's column → the EXPORT column (#18) it
 * shows on screen. The export vocabulary is the api's own (`EXPORT_COLUMN_IDS` of
 * `apps/api/src/exports/columns.ts`, snake_case) and the register's ids are the web's
 * (`REGISTER_HEADERS` above, camelCase) — this map is the ONE bridge, and a register
 * column with no entry simply has no export column («Дней в статусе», «Дней с ЕВИ»,
 * «Дедлайн договора» and «Обновлено» are derived columns the sheet does not carry).
 * Kept in sync by this comment and the unit spec, the same way the api's
 * `STATUS_RU_LABEL` keeps its web twin — neither side can import the other.
 */
export const EXPORT_COLUMN_OF_REGISTER: Readonly<Record<string, string>> = Object.freeze({
  pguId: 'pgu_id',
  fullName: 'full_name',
  phone: 'phone',
  email: 'email',
  status: 'status',
  venue: 'venue',
  courseId: 'course_id',
  submittedAt: 'submitted_at',
  classCourse: 'class_course',
  format: 'format',
  eviDeadline: 'evi_deadline',
  age: 'age',
  contract: 'contract',
  difficultyLevel: 'difficulty_level',
  algorithmGrade: 'algorithm_grade',
  languageGrade: 'language_grade',
  motivationGrade: 'motivation_grade',
  gradeSum: 'grade_sum',
  // ADDITIVE (v1.9.87, T.Z237) — «Трансферт»: the screen column rides to the file
  // (the api's appended `transfert_status`, the same verbatim word).
  transfertStatus: 'transfert_status',
  // ADDITIVE (v1.9.94, T.Z247) — the distribution block rides to the file (the api's
  // appended `group_venue` / `group_name` / `group_teacher` — the file appends them,
  // the screen keeps them next to «Площадка»; the api renders its own canonical
  // order either way, the ids choose the subset, never the order).
  groupVenueName: 'group_venue',
  groupName: 'group_name',
  groupTeacherName: 'group_teacher',
});

/**
 * ADDITIVE (v1.9.79, T.Z220) — the export columns with NO screen counterpart (the
 * api's ids; «№» included, the row index): not the user's to hide, so they ride along
 * whenever a subset is asked at all. Kept beside the map above for the same reason —
 * one bridge, one comment, one spec.
 */
export const EXPORT_ONLY_COLUMN_IDS: ReadonlyArray<string> = Object.freeze([
  'row_number',
  'unti_id',
  'region',
  'program',
  'first_seen_at',
  'flags',
  'manager_status',
  'contract_signed_at',
  'enrollment',
  'evi_failed',
]);

/**
 * ADDITIVE (v1.9.79, T.Z220) — the `?columns=` value of the view on screen: the export
 * column ids the CURRENTLY VISIBLE register columns map to (plus the export-only ones,
 * which the user cannot hide). `undefined` when NOTHING is hidden: the api then
 * renders every column, byte for byte the file it always answered, and the request
 * stays exactly what it was before T.Z220. The ORDER of the value is irrelevant — the
 * api renders the subset in its own canonical column order (the ids choose the
 * subset, never the order); this function still walks the table order, so the value
 * reads like the screen. Hiding a screen column narrows the file by exactly that
 * column and nothing else.
 *
 * `visible` is the T.Z219 model's own answer (`readVisibleColumns`), so the caller
 * never touches `localStorage` directly.
 */
export function exportColumnsParam(
  visible: ReadonlyArray<string>,
  columns: ReadonlyArray<RegisterColumn> = REGISTER_HEADERS,
): string | undefined {
  const hidden = new Set(defaultVisibleColumns(columns).filter((id) => !visible.includes(id)));
  if (hidden.size === 0) return undefined;
  const shown = new Set(visible);
  const ids = [
    // The mapped columns, in table order — only the VISIBLE ones.
    ...defaultVisibleColumns(columns)
      .filter((id) => shown.has(id) && EXPORT_COLUMN_OF_REGISTER[id] !== undefined)
      .map((id) => EXPORT_COLUMN_OF_REGISTER[id]!),
    // The export-only columns: no screen counterpart, nothing to hide.
    ...EXPORT_ONLY_COLUMN_IDS.filter((id) => id !== 'row_number'),
  ];
  return [...new Set(ids)].join(',');
}
